import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, GraduationCap, Building, CheckCircle, DollarSign, Gift, HandHeart, Mail, MapPin, Phone } from 'lucide-react';
import api from '../api';

const PartnerPage = () => {
  const [formData, setFormData] = useState({
    partner_type: 'individual',
    name: '',
    organization: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TN',
    zip_code: '',
    interest_type: 'donation',
    donation_amount: '',
    sponsorship_type: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/partners/register', formData);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: 'var(--success)', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Heart size={40} color="white" />
          </div>
          <h2 style={{ marginBottom: '16px' }}>Thank You for Your Support!</h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
            Your partnership inquiry has been received. Our team will contact you within 2-3 business 
            days to discuss how you can make a difference in healthcare education.
          </p>
          <div className="alert alert-info" style={{ textAlign: 'left', marginBottom: '24px' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>
              <strong>To Make a Donation:</strong><br />
              Please make checks payable to:<br />
              <strong>True Victory Ministries</strong><br />
              3150 E. Shelby Dr.<br />
              Memphis, TN 38118
            </p>
          </div>
          <Link to="/" className="btn btn-primary">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="partner-page" data-testid="partner-page">
      {/* Header */}
      <div className="gov-header">
        Tennessee Department of Labor &amp; Workforce Development Registered Training Provider
      </div>
      
      <nav className="nav-bar">
        <Link to="/" className="nav-logo">
          <div className="nav-logo-icon">MC</div>
          <div className="nav-logo-text">
            <span className="nav-logo-title">MedCare Training Academy</span>
            <span className="nav-logo-subtitle">Apprenticeship Program</span>
          </div>
        </Link>
        <div className="nav-links">
          <Link to="/#programs" className="nav-link">Programs</Link>
          <Link to="/employers" className="nav-link">Hire Our Graduates</Link>
          <Link to="/login" className="btn btn-primary btn-sm">Student Portal</Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero" style={{ paddingBottom: '40px', background: 'linear-gradient(135deg, #1e3a5f 0%, #0c4a6e 100%)' }}>
        <div className="hero-badge" style={{ background: '#dc2626' }}>
          <Heart size={16} />
          Community Initiative
        </div>
        <h1>Become a MedCare<br />Partner</h1>
        <p className="tagline">
          Join us in transforming lives through healthcare education. Your support helps provide 
          training opportunities to individuals seeking careers in nursing and healthcare.
        </p>
        
        {/* Community Initiative Banner */}
        <div className="enrollment-banner" style={{ background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.3)' }}>
          <div className="enrollment-badge" style={{ background: '#dc2626', color: 'white' }}>COMMUNITY SPONSOR</div>
          <div className="enrollment-content">
            <h3>MedCare Training Academy Community Initiative</h3>
            <p>Proudly sponsored by True Victory Ministries</p>
            <p style={{ fontSize: '0.8125rem', opacity: 0.9 }}>A State of Tennessee Non-Profit Organization</p>
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className="features-section" style={{ paddingTop: '60px' }}>
        <h2 className="section-title">Ways to Partner With Us</h2>
        <p className="section-subtitle">
          Whether you&apos;re an individual, business, or institution, there are many ways to support 
          our mission of creating healthcare professionals in Tennessee.
        </p>
        
        <div className="features-grid">
          <div className="feature-card" data-testid="donate-card">
            <div className="feature-icon" style={{ background: '#dc2626' }}>
              <Gift size={28} />
            </div>
            <h3>Make a Donation</h3>
            <p>One-time or recurring donations help fund scholarships, training materials, and facility improvements.</p>
          </div>
          
          <div className="feature-card" data-testid="sponsor-card">
            <div className="feature-icon" style={{ background: '#7c3aed' }}>
              <GraduationCap size={28} />
            </div>
            <h3>Sponsor a Student</h3>
            <p>Cover one full $99 online course, the full GED path, or an entire cohort. Change a life by investing in their apprenticeship training.</p>
          </div>
          
          <div className="feature-card" data-testid="corporate-card">
            <div className="feature-icon" style={{ background: 'var(--primary-blue)' }}>
              <Building size={28} />
            </div>
            <h3>Corporate Partnership</h3>
            <p>Businesses can sponsor cohorts, provide equipment, or establish ongoing scholarship funds.</p>
          </div>
          
          <div className="feature-card" data-testid="volunteer-card">
            <div className="feature-icon" style={{ background: 'var(--success)' }}>
              <HandHeart size={28} />
            </div>
            <h3>Volunteer Support</h3>
            <p>Healthcare professionals can volunteer as guest speakers, mentors, or clinical instructors.</p>
          </div>
        </div>
      </section>

      {/* Donation Information */}
      <section className="info-section" style={{ background: '#fef2f2' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ color: '#991b1b' }}>
            <Heart size={28} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            How to Donate
          </h2>
          
          <div className="card" style={{ padding: '32px', background: 'white', borderLeft: '4px solid #dc2626' }}>
            <p style={{ fontSize: '1.125rem', marginBottom: '20px', color: 'var(--gray-700)' }}>
              All donations to support the <strong>MedCare Training Academy Community Initiative</strong> should 
              be made to the order of its community sponsor:
            </p>
            
            <div style={{ 
              background: '#fef2f2', 
              padding: '24px', 
              borderRadius: '12px', 
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#991b1b', marginBottom: '8px', fontSize: '1.5rem' }}>True Victory Ministries</h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: '4px' }}>A State of Tennessee Non-Profit Organization</p>
              <div style={{ marginTop: '16px', color: 'var(--gray-700)' }}>
                <p style={{ margin: '4px 0', fontWeight: '500' }}>Mailing Address:</p>
                <p style={{ margin: '4px 0' }}>3150 E. Shelby Dr.</p>
                <p style={{ margin: '4px 0' }}>Memphis, TN 38118</p>
              </div>
            </div>

            <div className="alert alert-info">
              <p style={{ margin: 0 }}>
                <strong>Tax Deductible:</strong> True Victory Ministries is a registered 501(c)(3) non-profit 
                organization. Your donation may be tax-deductible. Please consult your tax advisor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Levels */}
      <section className="programs-section" style={{ background: 'var(--off-white)' }}>
        <h2 className="section-title">Student Sponsorship Levels</h2>
        <p className="section-subtitle">
          Every online course is a flat $99. Sponsor one student, a full GED path, an entire cohort,
          or define your own amount — every contribution makes a difference.
        </p>

        <div className="programs-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <div className="program-card" data-testid="sponsor-single-course">
            <span className="program-badge" style={{ background: '#0ea5e9' }}>One Student</span>
            <h3>Single-Course Sponsorship</h3>
            <p className="program-duration" style={{ fontSize: '1.6rem', color: 'var(--primary-blue)' }}>$99</p>
            <p>Sponsor one full online course for a student — CMA Training, PN, Phlebotomy, EMT, Homeland Security, Medical Billing & Coding, or any of our 5 Doctorate tracks.</p>
            <ul className="program-features">
              <li>Covers the flat $99 course enrollment</li>
              <li>Thank-you note from the student</li>
              <li>Recognition in partner newsletter</li>
            </ul>
          </div>

          <div className="program-card" data-testid="sponsor-full-path">
            <span className="program-badge" style={{ background: '#7c3aed' }}>Full Path</span>
            <h3>Full-Path Sponsorship</h3>
            <p className="program-duration" style={{ fontSize: '1.6rem', color: 'var(--primary-blue)' }}>$99</p>
            <p>Sponsor one student's full $99 program-enrollment fee. Mid-term and final exams are $99 each; the deferred tuition stays with MedCare under the ETA-671 Apprenticeship Agreement.</p>
            <ul className="program-features">
              <li>Covers $99 program enrollment</li>
              <li>$99 mid-term &amp; $99 final exams included</li>
              <li>Named recognition at graduation</li>
              <li>Certificate of appreciation</li>
            </ul>
          </div>

          <div className="program-card featured" data-testid="sponsor-cohort">
            <span className="program-badge" style={{ background: '#eab308' }}>Cohort</span>
            <h3>Cohort Sponsorship</h3>
            <p className="program-duration" style={{ fontSize: '1.6rem', color: 'var(--success)' }}>$990</p>
            <p>Sponsor <strong>10 students</strong> through one online course at our flat $99 rate.</p>
            <ul className="program-features">
              <li>10 × $99 course seats</li>
              <li>Plaque of recognition</li>
              <li>Graduation-ceremony invitation</li>
              <li>Named scholarship option</li>
            </ul>
          </div>

          <div className="program-card" data-testid="sponsor-full-path-cohort">
            <span className="program-badge" style={{ background: '#0ea5e9' }}>Full-Path Cohort</span>
            <h3>Full-Path Cohort</h3>
            <p className="program-duration" style={{ fontSize: '1.6rem', color: 'var(--primary-blue)' }}>$2,970</p>
            <p>Sponsor <strong>10 students</strong> end-to-end (course + mid-term + final). Ideal for a full GED graduating class.</p>
            <ul className="program-features">
              <li>10 × ($99 + $99 + $99)</li>
              <li>VIP graduation recognition</li>
              <li>Ongoing progress updates</li>
              <li>Co-branded scholarship name</li>
            </ul>
          </div>

          <div className="program-card" data-testid="sponsor-custom">
            <span className="program-badge" style={{ background: '#6b7280' }}>Custom</span>
            <h3>Custom Sponsorship</h3>
            <p className="program-duration" style={{ fontSize: '1.6rem', color: 'var(--primary-blue)' }}>You choose</p>
            <p>Define your own sponsorship amount — cover supplies, support a reentry program, or build a named endowment.</p>
            <ul className="program-features">
              <li>Any amount — we'll allocate it transparently</li>
              <li>Recognition matched to contribution size</li>
              <li>Tax-deductible via our non-profit partner</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section className="info-section">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="section-title">Partner Inquiry Form</h2>
          <p className="section-subtitle">
            Interested in partnering with us? Fill out the form below and our team will contact you 
            to discuss partnership opportunities.
          </p>

          <form onSubmit={handleSubmit} className="card" style={{ padding: '32px' }}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              
              {/* Partner Type */}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">I am a(n) *</label>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="partner_type"
                      value="individual"
                      checked={formData.partner_type === 'individual'}
                      onChange={handleChange}
                      data-testid="individual-radio"
                    />
                    Individual
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="partner_type"
                      value="business"
                      checked={formData.partner_type === 'business'}
                      onChange={handleChange}
                      data-testid="business-radio"
                    />
                    Business
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="partner_type"
                      value="institution"
                      checked={formData.partner_type === 'institution'}
                      onChange={handleChange}
                      data-testid="institution-radio"
                    />
                    Institution/Organization
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="partner_type"
                      value="church"
                      checked={formData.partner_type === 'church'}
                      onChange={handleChange}
                      data-testid="church-radio"
                    />
                    Church/Ministry
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Full name"
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Organization (if applicable)</label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Company/Organization name"
                  data-testid="organization-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="email@example.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="(901) 555-0123"
                  required
                  data-testid="phone-input"
                />
              </div>

              {/* Interest Type */}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">I am interested in *</label>
                <select
                  name="interest_type"
                  value={formData.interest_type}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="interest-select"
                >
                  <option value="donation">Making a Donation</option>
                  <option value="student_sponsorship">Sponsoring a Student</option>
                  <option value="corporate_partnership">Corporate Partnership</option>
                  <option value="volunteer">Volunteering</option>
                  <option value="equipment_donation">Equipment/Supplies Donation</option>
                  <option value="other">Other Partnership Opportunity</option>
                </select>
              </div>

              {formData.interest_type === 'student_sponsorship' && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Sponsorship Level</label>
                  <select
                    name="sponsorship_type"
                    value={formData.sponsorship_type}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="sponsorship-select"
                  >
                    <option value="">Select a level</option>
                    <option value="single_course">Single-Course Sponsorship ($99)</option>
                    <option value="full_path">Full-Path Sponsorship — covers $99 program enrollment ($99)</option>
                    <option value="cohort">Cohort — 10 seats ($990)</option>
                    <option value="full_path_cohort">Full-Path Cohort — 10 seats end-to-end ($2,970)</option>
                    <option value="custom">Custom Sponsorship (you choose the amount)</option>
                  </select>
                </div>
              )}

              {formData.interest_type === 'donation' && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Intended Donation Amount (Optional)</label>
                  <input
                    type="text"
                    name="donation_amount"
                    value={formData.donation_amount}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., $99 or $990"
                    data-testid="donation-amount-input"
                  />
                </div>
              )}

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Message (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-input"
                  rows={4}
                  placeholder="Tell us more about how you'd like to partner with MedCare Training Academy..."
                  data-testid="message-input"
                />
              </div>

              <div style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                <button 
                  type="submit" 
                  className="btn btn-primary btn-lg" 
                  style={{ width: '100%', background: '#dc2626' }}
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Partnership Inquiry'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section className="info-section" style={{ background: 'var(--off-white)' }}>
        <h2 className="section-title">Contact Us</h2>
        <div className="info-grid" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="info-card" style={{ textAlign: 'center' }}>
            <Mail size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
            <h3>Email</h3>
            <p><a href="mailto:medcare.nursing@yahoo.com">medcare.nursing@yahoo.com</a></p>
          </div>
          <div className="info-card" style={{ textAlign: 'center' }}>
            <Phone size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
            <h3>Phone</h3>
            <p><a href="tel:+19016251201">(901) 625-1201</a></p>
          </div>
          <div className="info-card" style={{ textAlign: 'center' }}>
            <MapPin size={32} style={{ color: '#dc2626', marginBottom: '12px' }} />
            <h3>Donations Mailing Address</h3>
            <p style={{ fontSize: '0.9375rem' }}>
              True Victory Ministries<br />
              3150 E. Shelby Dr.<br />
              Memphis, TN 38118
            </p>
          </div>
        </div>
      </section>

      {/* Workforce-development disclaimer */}

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <p data-testid="footer-copyright">
            &copy; 2026 MedCare Nursing Institute &amp; Training Academy. All rights reserved.
          </p>
          <p>
            A program of the <strong>MedCare National Board of Nursing &amp; Vital Health Sciences Association</strong> —
            3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
          </p>
          <p>Community Initiative sponsored by True Victory Ministries, a TN Non-Profit Organization.</p>
          <p>Programs approved and registered by the TN Dept of Labor Workforce Development Apprenticeship Program.</p>
          <p>Contact: <a href="mailto:medcare.nursing@yahoo.com">medcare.nursing@yahoo.com</a></p>
        </div>
      </footer>
    </div>
  );
};

export default PartnerPage;
